'use client';
import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { FAQS, type Faq } from '@/data/faqs';

interface FaqAccordionProps {
  /** Optional curated subset — defaults to the full FAQ list. */
  items?: Faq[];
  /** Optional heading override. */
  heading?: string;
  /** Hide the built-in heading (when the parent section provides its own). */
  showHeader?: boolean;
}

export default function FaqAccordion({
  items,
  heading = 'Frequently asked questions',
  showHeader = true,
}: FaqAccordionProps) {
  const FAQ_ITEMS = items ?? FAQS;
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <section id="faq" aria-labelledby={showHeader ? 'faq-heading' : undefined}>
      {showHeader && (
        <div className="section-head">
          <span className="eyebrow">Questions</span>
          <h2 id="faq-heading" className="text-2xl md:text-3xl text-foreground mt-2">
            {heading}
          </h2>
        </div>
      )}

      {/* A hairline-ruled list — open state is marked by the sign, not a fill */}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        {FAQ_ITEMS.map((item) => {
          const isOpen = openId === item.id;
          return (
            <div key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className="w-full flex items-start justify-between gap-5 py-4 text-left group"
                aria-expanded={isOpen}
                aria-controls={`${item.id}-panel`}
                id={`${item.id}-button`}
              >
                <span className="text-[0.9375rem] font-medium text-foreground leading-snug">
                  {item.question}
                </span>
                <span className="text-faint group-hover:text-foreground transition-colors mt-0.5 shrink-0">
                  {isOpen ? (
                    <Minus size={15} aria-hidden="true" />
                  ) : (
                    <Plus size={15} aria-hidden="true" />
                  )}
                </span>
              </button>
              {isOpen && (
                <div
                  id={`${item.id}-panel`}
                  role="region"
                  aria-labelledby={`${item.id}-button`}
                  className="pb-5 pr-10 animate-fade-in"
                >
                  <p className="text-sm text-muted-foreground leading-[1.7]">{item.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
